/**
 * Картка колеса на головній — широка, з живим колесом (owner 2026-09-01:
 * «колесо має бути видно, це не кнопка; перший блок має бути широкий,
 * великий, обʼємний»).
 *
 * Історія: спершу колесо стояло в кутку шапки двоповерховим віджетом і
 * ламало рядок, потім я звів його до таблетки — і сторінка стала порожньою.
 * Тепер це повноцінна картка на всю ширину: текст ліворуч, диск праворуч
 * виїжджає за край, під ним тепле світіння.
 */
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMe, useWheelState } from "@/api/hooks";
import { useCountdown } from "@/lib/countdown";
import { WheelNoSpinPopup } from "@/components/WheelNoSpinPopup";
import { spinsLabel } from "@/lib/spinsWord";


const N = 12;
const STEP = 360 / N;
const R = 88;

function sectorPath(i: number): string {
  const a0 = ((-90 + i * STEP) * Math.PI) / 180;
  const a1 = ((-90 + (i + 1) * STEP) * Math.PI) / 180;
  return `M100,100 L${(100 + R * Math.cos(a0)).toFixed(2)},${(100 + R * Math.sin(a0)).toFixed(2)} A${R},${R} 0 0 1 ${(100 + R * Math.cos(a1)).toFixed(2)},${(100 + R * Math.sin(a1)).toFixed(2)} Z`;
}

/** Коли колесо відкриється наступного разу, якщо сервер ще не відповів.
 *  Нарахування — опівночі 5 числа за Києвом, тобто 4-го о 21:00 UTC.
 *  Без цього таблетка з таймером лишалась порожньою, поки йде запит
 *  (owner 2026-09-05: «нет таймера колеса»). */
function nextGrantFallback(): string {
  const now = new Date();
  const d = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 4, 21, 0, 0),
  );
  if (d.getTime() <= now.getTime()) d.setUTCMonth(d.getUTCMonth() + 1);
  return d.toISOString();
}

export function WheelHeroCard() {
  // Стан бере сервер — інакше картка рахувала до 1 числа, а попап казав
  // «5 числа», і числа розходились (owner 2026-09-04).
  const state = useWheelState();
  // Профіль уже вміє відповідати про колесо — і він приходить ПЕРШИМ, ще до
  // першого кадру головної. Тому надпис правильний одразу, а окремий запит
  // лише уточнює його потім (owner 2026-09-05: «спочатку через тридцять днів,
  // а потім доступний оберт»).
  const me = useMe();
  // Той самий підхід, що й на головній: поки відповідь їде, показуємо останній
  // відомий стан, а не порожньо (owner 2026-09-05 — моргання).
  // ⚠️ Памʼять тут НЕ використовуємо. Показувати «1 безкоштовний оберт» із
  // минулого разу — це показувати неправду: людина вже могла крутнути, і за
  // мить таблетка перескакувала на «через 30 днів» (owner 2026-09-05).
  // Поки відповідь їде, показуємо чесний відлік — він правильний завжди.
  const ready =
    state.data?.available ?? me.data?.wheel_available ?? false;
  // Коли оберту немає, картка не веде на колесо, а чесно каже, коли він буде
  // (owner 2026-09-05). Поки сервер не відповів — пускаємо на сторінку, там
  // рішення ухвалює вона сама.
  // Лічильник обертів (owner 2026-09-24): після покупки пакета їх може бути
  // кілька. Єдиний місячний — так і називаємо «безкоштовним», як раніше.
  // Поки /wheel їде, беремо лічильник із профілю — він приходить першим.
  // Раніше тут стояла здогадка «1», і картка за кілька секунд перескакувала
  // з «1 безкоштовний оберт» на «2 оберти» (owner 2026-09-25).
  const spinsLeft =
    state.data?.spins_left ?? me.data?.wheel_spins_left ?? (ready ? 1 : 0);
  const freeLeft =
    state.data?.free_left ?? me.data?.wheel_free_left ?? (ready ? 1 : 0);
  const badge =
    spinsLeft === 1 && freeLeft === 1
      ? "1 безкоштовний оберт"
      : spinsLabel(Math.max(spinsLeft, 1));
  const [noSpinOpen, setNoSpinOpen] = useState(false);
  const navigate = useNavigate();
  const btnRef = useRef<HTMLButtonElement | null>(null);


  // Рішення ухвалюємо в момент натискання, а не під час рендеру: людина часто
  // тисне картку РАНІШЕ, ніж прийшов стан колеса, і тоді сторінка відкривалась
  // дарма (owner 2026-09-05: «сторінка з колесом усе ще відкривається»).
  // ⚠️ Дію вішаємо на pointerup, а не на click. Діагностика на проді показала:
  // pointerdown до кнопки доходить, а click НЕ настає — усередині Telegram
  // дотик по цій картці перетворюється на жест і клік скасовується
  // (owner 2026-09-05: «нажимаю и нихуя»). onClick лишається запасним шляхом,
  // подвійного спрацювання не буде — тримає прапорець.
  const firing = useRef(false);

  const open = async () => {
    if (firing.current) return;
    firing.current = true;
    setTimeout(() => {
      firing.current = false;
    }, 600);
    // Клік не має права нічого не зробити: якщо стан не приїхав або запит
    // упав — показуємо попап, а не мовчимо (owner 2026-09-05).
    let can = state.data?.available;
    try {
      if (can === undefined) {
        const fresh = await state.refetch();
        can = fresh.data?.available;
      }
    } catch {
      can = undefined;
    }
    // Явне false — кажемо, коли буде наступний. Якщо стан так і лишився
    // невідомим (запит упав), НЕ брешемо «оберту немає», а пускаємо на
    // сторінку: там рішення ухвалює вона (знайдено аудитом перед запуском).
    if (can === false) setNoSpinOpen(true);
    else navigate("/wheel");
  };
  // Поки далеко — днями, коли лишилась доба й менше — тікає таймер
  // (owner 2026-09-04).
  const left = useCountdown(
    state.data?.next_grant_at ??
      me.data?.wheel_next_grant_at ??
      nextGrantFallback(),
  );

  const card = (
    <>
      {/* Композиція та сама, що сподобалась: текст ліворуч, диск праворуч
          майже на всю висоту картки, під ним тепле світіння. Змінилась лише
          висота — тепер як у звичайної кнопки (owner 2026-09-01). */}
      <div
        className="pointer-events-none absolute -right-10 top-1/2 h-56 w-56 -translate-y-1/2 rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(255,161,53,0.34), rgba(255,161,53,0) 70%)",
        }}
      />

      <div className="relative z-10 min-w-0 flex-1 pr-28">
        <div className="font-display text-[0.9375rem] font-bold leading-tight text-ink">
          Колесо фортуни
        </div>
        <div className="mt-1 text-[0.71875rem] leading-snug text-ink-dim [text-wrap:balance]">
          Крути безкоштовно раз на місяць
        </div>
        <div
          className={
            "mt-2 inline-flex items-center rounded-full px-2.5 py-0.5 font-display text-[0.65625rem] font-bold " +
            (ready ? "bg-primary text-white" : "bg-primary/10 text-primary")
          }
        >
          {ready ? badge : left}
        </div>
      </div>

      {/* Диск удвічі більший і навмисно виходить за правий край — видно
          приблизно половину колеса (owner 2026-09-03). Обрізає його
          overflow-hidden самої картки. */}
      <div className="pointer-events-none absolute right-[-2.5rem] top-1/2 z-10 h-[8.5rem] w-[8.5rem] -translate-y-1/2">
        {/* Повертаємо на чверть: стрілка стає ліворуч і завжди видно, що це
            саме колесо фортуни, а не просто диск (owner 2026-09-03). */}
        <svg
          viewBox="0 0 200 200"
          className="block h-full w-full"
          style={{ transform: "rotate(-90deg)" }}
        >
          <defs>
            <radialGradient id="whcFace" cx="36%" cy="26%" r="80%">
              <stop offset="0%" stopColor="#FFFDFA" />
              <stop offset="100%" stopColor="#F2EADE" />
            </radialGradient>
            <linearGradient id="whcWin" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FFA135" />
              <stop offset="100%" stopColor="#FE6B04" />
            </linearGradient>
            <filter id="whcDrop" x="-30%" y="-30%" width="160%" height="160%">
              <feDropShadow dx="0" dy="6" stdDeviation="7" floodColor="#3B2A18" floodOpacity="0.20" />
            </filter>
          </defs>
          <circle cx="100" cy="100" r={R} fill="url(#whcFace)" filter="url(#whcDrop)" />
          {/* Доворот рівно на ПІВСЕКТОРА (owner 2026-09-03): стрілка
              потрапляє в середину сектора, а не в стик двох. Щоб під нею був
              саме бежевий, парність заливки нижче перевернута.
              Диск НЕ обертається (owner 2026-09-04: «я не просил делать чтоб
              он крутился, это немного лишне»). */}
          <g transform="rotate(-15 100 100)">
          <g>
            {Array.from({ length: N }, (_, i) => (
              <path
                key={i}
                d={sectorPath(i)}
                fill={i === 11 ? "url(#whcWin)" : i % 2 === 0 ? "#F5EDE0" : "#FFFFFF"}
              />
            ))}
            {Array.from({ length: N }, (_, i) => {
              const a = ((-90 + i * STEP) * Math.PI) / 180;
              return (
                <circle
                  key={`p${i}`}
                  cx={(100 + R * Math.cos(a)).toFixed(2)}
                  cy={(100 + R * Math.sin(a)).toFixed(2)}
                  r={4.5}
                  fill="#FFFFFF"
                  stroke="#D9CDBA"
                  strokeWidth={1.5}
                />
              );
            })}
            <circle cx="100" cy="100" r="28" fill="#FFFFFF" />
            <circle cx="100" cy="100" r="28" fill="none" stroke="rgba(23,20,17,.10)" />
            <circle cx="100" cy="100" r="9" fill="#FE6B04" />
          </g>
          </g>
          <path d="M100 16 l12,-22 h-24 z" fill="#171411" />
        </svg>
      </div>
    </>
  );

  const shell =
    "relative flex min-h-[6rem] w-full items-center overflow-hidden " +
    "rounded-ios-card bg-surface px-4 py-3.5 text-left shadow-card " +
    "active:scale-[0.99] transition";

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={shell}
        onPointerUp={open}
        onClick={open}
      >
        {card}
      </button>
      <WheelNoSpinPopup
        open={noSpinOpen}
        onClose={() => setNoSpinOpen(false)}
        nextAt={state.data?.next_grant_at}
      />
    </>
  );
}
